import {safeText} from "../js/util/CommonUtils.js";

export default class HtmlUtils {
    static getParentSvgSize(parentElement) {
        const parentSvg = parentElement.closest('svg');
        if(!parentSvg)
            throw new Error(parentElement + ' was not SVG and did not have SVG as one of its parents');
        return HtmlUtils.getBoundingRect(parentSvg);
    }
    static getBoundingRect(htmlElement) {
        const clientRect = htmlElement.getBoundingClientRect();
        if(!this._isPositiveNum(clientRect.width) || !this._isPositiveNum(clientRect.height))
            // throw new Error(this.getHtmlElementPath(htmlElement) + ' has no width or height: ' + JSON.stringify(clientRect));
            console.warn(this.getHtmlElementPath(htmlElement) + ' has no width or height: ' + JSON.stringify(clientRect));
        return clientRect;
    }

    /**
     * @param {Element} htmlElement
     */
    static getHtmlElementPath(htmlElement) {
        const path = [];
        let element = htmlElement;
        while(element && element.tagName) {// apparently <html> has a parent, but it doesn't have a tag name
            let classes = element.classList.value ? '.' + element.classList.value : '';
            path.push(element.tagName + classes);
            element = element.parentNode;
        }
        return path.reverse().join(' > ');
    }
    static _isPositiveNum(num) {
        return typeof num === 'number' && num > 0;
    }

    /**
     * @param {HTMLElement} pageContentElement
     * @param {HttpError} httpError
     */
    static onPageDataAccessExceptionThrown(pageContentElement, httpError) {
        const {securityContext, isForbidden, isUnauthorized, isNotFound, isLicenseRequired} = httpError;
        if (isUnauthorized || isForbidden) {
            let actionUrl = securityContext.mainPageUrl;
            let actionName = 'Main page';
            if (!securityContext.isAuthenticated()) {
                actionUrl = securityContext.signInUrl;
                actionName = 'Sign In';
            }
            pageContentElement.innerHTML = HtmlUtils.accessDeniedHtmlTemplate(actionUrl, actionName);
        } else if (isNotFound) {
            pageContentElement.innerHTML = HtmlUtils.resourceNotFoundHtmlTemplate(securityContext.mainPageUrl, 'Main page');
        } else if (isLicenseRequired) {
            pageContentElement.innerHTML = HtmlUtils.licenseRequiredHtmlTemplate(httpError);
        }
    }

    /**
     * @param {string} actionUrl
     * @param {string} actionName
     * @returns {string}
     */
    static accessDeniedHtmlTemplate(actionUrl, actionName) {
        return `
            <div class="resource-not-found-message-container">
                <span class="resource-not-found-message-container__text js-text">You don't have the required permissions to access this page.</span>
                <a class="js-sign-in-link" href="${actionUrl}">${actionName}</a>
            </div>`;
    }
    /**
     * @param {string} actionUrl
     * @param {string} actionName
     * @returns {string}
     */
    static resourceNotFoundHtmlTemplate(actionUrl, actionName) {
        return `
            <div class="resource-not-found-message-container">
                <span class="resource-not-found-message-container__text js-text">The page doesn't exist.</span>
                <a class="js-action-link" href="${actionUrl}">${actionName}</a>
            </div>`;
    }

    /**
     * The server only tells us *why* usage is blocked (via {@link HttpError#isSeatsExceeded}); the explanation
     * and instructions for regular users vs. admins are hardcoded here rather than sent from the server.
     * @param {HttpError} httpError
     * @returns {string}
     */
    static licenseRequiredHtmlTemplate(httpError) {
        const {securityContext, isSeatsExceeded} = httpError;
        const isAdmin = securityContext.isAuthenticated() && securityContext.getCurrentUser().isAdmin();
        const summary = isSeatsExceeded
            ? "The number of active user accounts exceeds your license's seat limit."
            : 'No valid license is currently in effect.';
        const adminAction = isSeatsExceeded
            ? 'Deactivate accounts or upload a license with more seats to restore access for everyone.'
            : 'Upload a valid license to restore access for everyone.';
        const message = isAdmin
            ? `${summary} ${adminAction}`
            : 'This application is currently unavailable due to a licensing issue. Contact your administrator.';
        const actionUrl = isAdmin ? '/settings?screen=license' : securityContext.mainPageUrl;
        const actionLabel = isAdmin ? 'Go to License settings' : 'Main page';
        return `
            <div class="resource-not-found-message-container">
                <div class="license__notice license__notice--error">
                    <span class="material-symbols-outlined" aria-hidden="true">error</span>
                    <span class="license__notice-text js-text">${safeText(message)}</span>
                </div>
                <a class="js-action-link" href="${actionUrl}">${actionLabel}</a>
            </div>`;
    }

    /**
     * Only for backward compatibility, until all usages are replaced with new HTML5Dialog's
     * This fixes a bug, when something is present in the DOM tree after the modal element.
     * Since we have css rule that shows only the last in the dom modal, they are not visible.
     * @deprecated
     * @param {HTMLElement} el
     */
    static createModal(el){
            const modal = document.createElement('div');
            modal.classList.add('modal');
            modal.setAttribute('tabIndex', '0')
            modal.append(el);
            let container = document.querySelector('.modal-container');
            if (!container) {
                container = document.createElement('div');
                container.classList.add('modal-container');
                document.body.append(container);
            }
            container.append(modal);
            return modal;
    }

    static inlineStyles(source, target) {
        // Empty iframe gives per-tag browser-default styles, so we only inline properties
        // our CSS actually changed - inlining all ~350 computed properties on every node
        // produces multi-megabyte SVGs.
        const iframe = document.createElement('iframe');
        iframe.style.display = 'none';
        document.body.append(iframe);
        const defaultsCache = new Map();
        try {
            HtmlUtils.#inlineChangedStyles(source, target, iframe.contentDocument, defaultsCache);
        } finally {
            iframe.remove();
        }
    }

    static #inlineChangedStyles(source, target, defaultsDoc, defaultsCache) {
        if (source.nodeType === Node.ELEMENT_NODE) {
            const tag = source.tagName;
            if (!defaultsCache.has(tag)) {
                const probe = defaultsDoc.createElementNS(source.namespaceURI, tag);
                defaultsDoc.body.append(probe);
                defaultsCache.set(tag, getComputedStyle(probe));
            }
            const defaults = defaultsCache.get(tag);
            const computed = getComputedStyle(source);
            const style = Array.from(computed)
                .filter(name => computed.getPropertyValue(name) !== defaults.getPropertyValue(name))
                .map(name => `${name}:${computed.getPropertyValue(name)};`).join('');
            if (style)
                target.setAttribute('style', style);
        }
        Array.from(source.children).forEach((child, index) => {
            const targetChild = target.children[index];
            if (targetChild)
                HtmlUtils.#inlineChangedStyles(child, targetChild, defaultsDoc, defaultsCache);
        });
    }
}